import type { DeliverableType, Platform } from '@k2/domain/campaigns'
import type { EditableDeliverableStatus } from '@k2/domain/deliverable-status'
import { useApi } from '@/api'

export type DeliverableChanges = { type?: DeliverableType; status?: EditableDeliverableStatus; topic?: string }

export const useDeliverablesResource = () => {
  const api = useApi()
  const deliverableOf = (organizationId: string, deliverableId: string) =>
    api.api.v1.organizations({ organizationId }).deliverables({ deliverableId })

  const getCampaignDeliverables = (organizationId: string, campaignId: string) =>
    api.api.v1.organizations({ organizationId }).campaigns({ campaignId }).deliverables.get()
  const setCampaignDeliverable = (organizationId: string, campaignId: string, draft: { topic?: string; type?: DeliverableType } = {}) =>
    api.api.v1.organizations({ organizationId }).campaigns({ campaignId }).deliverables.post(draft)

  // `to` is exclusive, and the one array carries the backlog (`scheduledAt === null`) alongside the window.
  const getCalendarDeliverables = (organizationId: string, from: string, to: string) =>
    api.api.v1.organizations({ organizationId }).deliverables.get({ query: { from, to } })

  const getDeliverable = (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId).get()
  const delDeliverable = (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId).delete()
  const setDeliverableDuplicate = (organizationId: string, deliverableId: string) =>
    deliverableOf(organizationId, deliverableId).duplicate.post()
  const updateDeliverable = (organizationId: string, deliverableId: string, changes: DeliverableChanges) =>
    deliverableOf(organizationId, deliverableId).patch(changes)
  const getDeliverablePlatforms = (organizationId: string, deliverableId: string) =>
    deliverableOf(organizationId, deliverableId).platforms.get()

  const setDeliverablePlatforms = (organizationId: string, deliverableId: string, platforms: Platform[]) =>
    deliverableOf(organizationId, deliverableId).platforms.put({ platforms })

  const setDeliverableSchedule = (organizationId: string, deliverableId: string, scheduledAt: string | null) =>
    deliverableOf(organizationId, deliverableId).schedule.patch({ scheduledAt })

  // Without a choice the canvas is built from the start the post already has.
  const setPostStart = (organizationId: string, deliverableId: string) => deliverableOf(organizationId, deliverableId).start.post()

  return {
    getCampaignDeliverables,
    setCampaignDeliverable,
    getCalendarDeliverables,
    getDeliverable,
    delDeliverable,
    setDeliverableDuplicate,
    updateDeliverable,
    getDeliverablePlatforms,
    setDeliverablePlatforms,
    setPostStart,
    setDeliverableSchedule
  }
}
